import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  
  const [status, setStatus] = useState('');
  const [contactInfo, setContactInfo] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiErrors, setApiErrors] = useState([]);

  useEffect(() => {
    loadContactData();
  }, []);

  const loadContactData = async () => {
    try {
      setLoading(true);
      const errors = [];
      const [contactResponse, pageResponse] = await Promise.allSettled([
        apiService.getContactInfo(),
        apiService.getPage('contact')
      ]);

      if (contactResponse.status === 'fulfilled') {
        const raw = contactResponse.value.data;
        const contactData = raw?.data || (raw?.success === false ? null : raw) || null;
        console.log('[CONTACT] Contact info from DB:', contactData);
        setContactInfo(contactData);
      } else {
        console.error('[CONTACT] Contact info API failed:', contactResponse.reason);
        errors.push('Contact info: ' + (contactResponse.reason?.message || 'API unreachable'));
      }

      if (pageResponse.status === 'fulfilled') {
        const raw = pageResponse.value.data;
        const pd = raw?.data || (raw?.success === false ? null : raw) || null;
        console.log('[CONTACT] Page content from DB:', pd);
        setPageData(pd);
      } else {
        console.error('[CONTACT] Page API failed:', pageResponse.reason);
        errors.push('Page content: ' + (pageResponse.reason?.message || 'API unreachable'));
      }

      setApiErrors(errors);
    } catch (err) {
      console.error('[CONTACT] Unexpected error:', err);
      setApiErrors(['Unexpected: ' + err.message]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await apiService.submitContact(formData);
      
      if (response.data.success) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus('error');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      {apiErrors.length > 0 && (
        <div style={{background:'#fff3cd', border:'1px solid #ffc107', padding:'12px 20px', fontSize:'13px', color:'#856404'}}>
          <strong>⚠️ Database not connected — showing static content.</strong> API Errors: {apiErrors.join(' | ')}
          {' '}<a href="http://localhost/api/diagnostic.php" target="_blank" rel="noreferrer" style={{color:'#0d6efd'}}>Run Diagnostic</a>
          {' | '}<a href="http://localhost/api/setup_schema.php?confirm=yes" target="_blank" rel="noreferrer" style={{color:'#dc3545'}}>Setup Database</a>
        </div>
      )}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-hero-title">{pageData?.title || 'Contact Us'}</h1>
          <p className="page-hero-subtitle">{pageData?.excerpt || 'Whatever brings you here, Sampoorna offers a space to pause, explore and reconnect.'}</p>
        </div>
      </section>

      <section className="contact-section">
        <div className="container">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2>Get in Touch</h2>
              <div className="contact-item">
                <div className="contact-icon">📞</div>
                <div className="contact-details">
                  <h3>Phone</h3>
                  <p><a href={`tel:${contactInfo?.phone || '+918130885755'}`}>{contactInfo?.phone || '+91-8130885755'}</a></p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">✉️</div>
                <div className="contact-details">
                  <h3>Email</h3>
                  <p><a href={`mailto:${contactInfo?.email || 'anchal.rana@globalsampoorna.com'}`}>{contactInfo?.email || 'anchal.rana@globalsampoorna.com'}</a></p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div className="contact-details">
                  <h3>Address</h3>
                  <p>{contactInfo?.address || 'C707, Siksha Bharti school road, near ramphal Chowk, Sec -7 Dwarka, Delhi 110077'}</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">🕒</div>
                <div className="contact-details">
                  <h3>Work Hours</h3>
                  <p>
                    <strong>Monday - Saturday:</strong> {contactInfo?.working_hours || '09:00am - 7:00 pm'}<br />
                    <strong>Sundays:</strong> Holiday
                  </p>
                </div>
              </div>
            </div>
            <div className="contact-form-wrapper">
              <h2>Send Us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Your Phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Service</option>
                    <option value="vedic-wellness">Vedic Wellness & Diet</option>
                    <option value="meditation-pranayama">Meditation & Pranayama</option>
                    <option value="vedic-astrology">Vedic Astrology</option>
                    <option value="bnn">Bhrigu Nandi Nadi Astrology</option>
                    <option value="astrotarot">AstroTarot</option>
                    <option value="vedic-numerology">Vedic Numerology</option>
                    <option value="vastu">Vastu Consultation</option>
                    <option value="mantra">Mantra & Spiritual Practices</option>
                    <option value="spiritual-guidance">Spiritual Guidance & Coaching</option>
                    <option value="learning">Workshops, Courses & Learning</option>
                  </select>
                </div>
                <div className="form-group">
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                {status === 'success' && (
                  <div style={{background:'#d1e7dd', color:'#0f5132', padding:'10px 15px', borderRadius:'6px', marginBottom:'10px'}}>
                    ✅ Message sent successfully! We'll get back to you soon.
                  </div>
                )}
                {status === 'error' && (
                  <div style={{background:'#f8d7da', color:'#842029', padding:'10px 15px', borderRadius:'6px', marginBottom:'10px'}}>
                    ❌ Failed to send message. Please try again or call us directly.
                  </div>
                )}
                <button type="submit" className="btn btn-primary" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
          <div className="map-container">
            <h2>Find Us</h2>
            <iframe
              title="Sampoorna Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.1234567890123!2d77.0589!3d28.6139!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM2JzUwLjAiTiA3N8KwMDMnMzIuMCJF!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
              width="100%"
              height="400"
              style={{ border: 0, borderRadius: '10px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
